import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, AlertCircle } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listPointRewards, deletePointReward } from "@/lib/api/admin";
import { formatCents } from "@/lib/utils/number";
import type { PointReward } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Spinner } from "@/components/Spinner";
import { StatusBadge } from "@/components/StatusBadge";
import { PointRewardFormDialog } from "../dialogs/PointRewardFormDialog";

type PointRewardsTabProps = {
  storeId: string;
  priceLimitBypass: boolean;
  storeCategory: string;
};

// ─── Main PointRewardsTab ─────────────────────────────────────────────────────

export function PointRewardsTab({
  storeId,
  priceLimitBypass,
  storeCategory,
}: PointRewardsTabProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingReward, setEditingReward] = useState<PointReward | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<PointReward | null>(null);

  const {
    data: rewards,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["point-rewards", storeId],
    queryFn: () => listPointRewards(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  // Deep-link auto-open: a Featured Rewards row links here with ?rewardId= to jump straight into that reward's edit dialog.
  useEffect(() => {
    const rewardId = searchParams.get("rewardId");
    if (!rewardId || !rewards) return;
    const match = rewards.find((r) => r.id === rewardId);
    if (match) {
      setEditingReward(match);
      setFormKey((k) => k + 1);
      setDialogOpen(true);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("rewardId");
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewards]);

  const deleteMutation = useMutation({
    mutationFn: (r: PointReward) => deletePointReward(user!, storeId, r.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["point-rewards", storeId] });
      setDeleteTarget(null);
      toast.success("Reward deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="size-5 border-primary" />
      </div>
    );
  }

  if (isError)
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <AlertCircle className="size-6 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Failed to load rewards.</p>
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Point Rewards ({rewards?.length ?? 0})</h3>
        <Button
          size="sm"
          onClick={() => {
            setEditingReward(undefined);
            setFormKey((k) => k + 1);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-3.5 mr-1" /> Add Reward
        </Button>
      </div>

      {rewards?.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No rewards yet.</p>
      ) : (
        <div className="space-y-2">
          {rewards?.map((r) => (
            <Card
              key={r.id}
              onClick={() => {
                setEditingReward(r);
                setFormKey((k) => k + 1);
                setDialogOpen(true);
              }}
              className="cursor-pointer hover:border-primary/50"
            >
              <CardContent className="flex items-start gap-3 p-3">
                {r.image_url && (
                  <img
                    src={r.image_url}
                    alt={r.name}
                    className="w-12 h-12 rounded object-cover shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{r.name}</p>
                    <StatusBadge status={r.status} />
                  </div>
                  {r.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">{r.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCents(r.price_cents)} · {r.points_required.toLocaleString()} pts
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(r);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PointRewardFormDialog
        key={formKey}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingReward(undefined);
        }}
        storeId={storeId}
        priceLimitBypass={priceLimitBypass}
        storeCategory={storeCategory}
        reward={editingReward}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title="Delete reward"
        description={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
      />
    </div>
  );
}
